import React from "react";
import { Box, Chip, Grid, Typography } from "@mui/material";
import {
  Badge,
  BusinessCenter,
  Email,
  EmojiEvents,
  Engineering,
  Language,
  Link as LinkIcon,
  LinkedIn,
  LocationOn,
  MenuBook,
  Person,
  Phone,
  School,
  VerifiedUser,
  Work,
} from "@mui/icons-material";
import moment from "moment";
import { DateRange, formatUrl, IconWrapper, SectionHeader, SectionWrapper } from "./UIComponents";

// Contact information section component
export const ContactSection = ({ contactInfo }) => {
  if (!contactInfo) return null;

  return (
    <Box
      sx={{
        background: "#f0f7ff",
        p: 3,
        borderRadius: 2,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        border: "1px solid #e2e8f0",
      }}
    >
      <Grid container spacing={1}>
        <Grid item xs={12}>
          {(contactInfo.firstName || contactInfo.lastName) && (
            <Typography variant="h4" fontWeight={700} mb={1}>
              {[contactInfo.firstName, contactInfo.lastName].filter(Boolean).join(" ")}
            </Typography>
          )}
        </Grid>

        {/* Address Row */}
        {(contactInfo.addressLineOne ||
          contactInfo.city ||
          contactInfo.state ||
          contactInfo.zipCode) && (
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <LocationOn
                fontSize="small"
                sx={{
                  fontSize: "1.2rem",
                  color: "#3b82f6",
                  mt: 0.7,
                }}
              />
              <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                {contactInfo.addressLineOne && (
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#334155",
                      fontWeight: 500,
                      fontSize: "1.05rem",
                    }}
                  >
                    {contactInfo.addressLineOne}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    fontSize: "0.9rem",
                    mt: 0.5,
                  }}
                >
                  {[contactInfo.city, contactInfo.state, contactInfo.zipCode]
                    .filter(Boolean)
                    .join(", ")}
                </Typography>
              </Box>
            </Box>
          </Grid>
        )}

        {/* Contact Information Section */}
        <Grid item xs={12}>
          <Box sx={{ mb: 1 }}>
            {/* Email Row */}
            <Grid container spacing={2} sx={{ mb: 1 }}>
              {contactInfo.email && (
                <Grid item xs={12} sm={6}>
                  <IconWrapper>
                    <Email fontSize="small" color="info" />
                    <span>{contactInfo.email}</span>
                  </IconWrapper>
                </Grid>
              )}
              {contactInfo.altEmail && (
                <Grid item xs={12} sm={6}>
                  <IconWrapper>
                    <Email fontSize="small" color="info" />
                    <span>{contactInfo.altEmail}</span>
                  </IconWrapper>
                </Grid>
              )}
            </Grid>

            {/* Phone Row */}
            <Grid container spacing={2}>
              {contactInfo.phone && (
                <Grid item xs={12} sm={6}>
                  <IconWrapper>
                    <Phone fontSize="small" color="info" />
                    <span>{contactInfo.phone}</span>
                  </IconWrapper>
                </Grid>
              )}
              {contactInfo.altPhone && (
                <Grid item xs={12} sm={6}>
                  <IconWrapper>
                    <Phone fontSize="small" color="info" />
                    <span>{contactInfo.altPhone}</span>
                  </IconWrapper>
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>

        {/* LinkedIn */}
        {contactInfo.linkedin && (
          <Grid item xs={12}>
            <Box sx={{ pt: 1, borderTop: "1px solid #e2e8f0" }}>
              <IconWrapper>
                <LinkedIn fontSize="small" color="info" />
                <a
                  href={formatUrl(contactInfo.linkedin)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#3b82f6", textDecoration: "none" }}
                >
                  {contactInfo.linkedin.replace(/^https?:\/\/(www\.)?/, "")}
                </a>
              </IconWrapper>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

// Career objective section component
export const ObjectiveSection = ({ objective, jobTitle }) => {
  if (!objective && !jobTitle) return null;

  return (
    <SectionWrapper>
      <SectionHeader icon={<MenuBook fontSize="medium" color="info" />} title="Career Objective" />
      {jobTitle && (
        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          {jobTitle}
        </Typography>
      )}
      {objective && <Typography sx={{ color: "gray" }}>{objective}</Typography>}
    </SectionWrapper>
  );
};

// Work history section component
export const WorkHistorySection = ({ jobHistory }) => {
  if (!jobHistory?.length) return null;

  return (
    <SectionWrapper>
      <SectionHeader icon={<Work fontSize="medium" color="info" />} title="Work History" />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {jobHistory.map((job) => (
          <Box
            key={`${job.jobTitle}-${job.companyName}`}
            sx={{ borderBottom: "1px solid #e5e7eb", pb: 2 }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {job.jobTitle || "Position"}
                </Typography>
                <Typography variant="body2" sx={{ color: "gray" }}>
                  {job.companyName}
                  {(job.city || job.state) &&
                    ` - ${[job.city, job.state].filter(Boolean).join(", ")}`}
                </Typography>
              </Box>
              <DateRange startDate={job.startDate} endDate={job.endDate} />
            </Box>
            {job.responsibilities?.length > 0 && (
              <ul style={{ paddingLeft: 20, marginTop: 8 }}>
                {job.responsibilities.map((resp) => (
                  <li key={resp.id || resp.description} style={{ color: "#4B5563" }}>
                    {typeof resp === "string" ? resp : resp.description}
                  </li>
                ))}
              </ul>
            )}
          </Box>
        ))}
      </Box>
    </SectionWrapper>
  );
};

// Education section component
export const EducationSection = ({ education }) => {
  if (!education?.length) return null;

  return (
    <SectionWrapper>
      <SectionHeader icon={<School fontSize="medium" color="info" />} title="Education" />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {education.map((edu) => (
          <Box
            key={`${edu.degree}-${edu.institutionName}`}
            sx={{ borderBottom: "1px solid #e5e7eb", pb: 2 }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {edu.degree}
                </Typography>
                <Typography variant="body2" sx={{ color: "gray" }}>
                  {edu.institutionName}
                  {(edu.city || edu.state) &&
                    ` - ${[edu.city, edu.state].filter(Boolean).join(", ")}`}
                </Typography>
                {edu.fieldOfStudy && (
                  <Typography variant="body2" sx={{ color: "gray" }}>
                    Major: {edu.fieldOfStudy}
                    {edu.minor && `, Minor: ${edu.minor}`}
                  </Typography>
                )}
              </Box>
              <DateRange startDate={edu.startDate} endDate={edu.endDate} />
            </Box>
          </Box>
        ))}
      </Box>
    </SectionWrapper>
  );
};

// Overall experience section
export const ExperienceSection = ({ overallExperience, industries }) => {
  if (
    (!overallExperience || (!overallExperience.years && !overallExperience.months)) &&
    (!industries || !industries.length)
  )
    return null;

  return (
    <SectionWrapper>
      <SectionHeader icon={<BusinessCenter fontSize="medium" color="info" />} title="Experience" />

      {overallExperience && (overallExperience.years || overallExperience.months) && (
        <Box mb={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            Overall Experience
          </Typography>
          <Typography variant="body2" sx={{ color: "gray" }}>
            {overallExperience.years > 0 && `${overallExperience.years} years`}
            {overallExperience.years > 0 && overallExperience.months > 0 && ` and `}
            {overallExperience.months > 0 && `${overallExperience.months} months`}
          </Typography>
        </Box>
      )}

      {industries && industries.length > 0 && (
        <>
          <Typography variant="subtitle1" fontWeight={600} mt={2}>
            Industry Experience
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {industries.map((industry, index) => (
              <Box key={industry.industryName || index}>
                <Typography variant="body2" fontWeight={500}>
                  {industry.industryName}
                </Typography>
                {industry.yearsOfExperience && (
                  <Typography variant="body2" sx={{ color: "gray" }}>
                    {industry.yearsOfExperience.years > 0 &&
                      `${industry.yearsOfExperience.years} years`}
                    {industry.yearsOfExperience.years > 0 &&
                      industry.yearsOfExperience.months > 0 &&
                      ` and `}
                    {industry.yearsOfExperience.months > 0 &&
                      `${industry.yearsOfExperience.months} months`}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </>
      )}
    </SectionWrapper>
  );
};

// Skills section component
export const SkillsSection = ({ skillsData }) => {
  if (!skillsData || (!skillsData.tags.length && !skillsData.otherSkills.length)) return null;

  return (
    <SectionWrapper>
      <SectionHeader icon={<Engineering fontSize="medium" color="info" />} title="Skills" />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {/* Tags with success color */}
        {skillsData.tags.map((skill) => (
          <Chip
            key={skill}
            label={skill}
            sx={{
              backgroundColor: "#ecfdf5",
              color: "#10b981",
              fontWeight: 600,
              fontSize: "0.8rem",
              border: "1px solid #a7f3d0",
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            }}
          />
        ))}

        {/* Other skills with current blue color */}
        {skillsData.otherSkills.map((skill) => (
          <Chip
            key={skill}
            label={skill}
            sx={{
              backgroundColor: "#eff6ff",
              color: "#2563EB",
              fontWeight: 600,
              fontSize: "0.8rem",
              border: "1px solid #bfdbfe",
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            }}
          />
        ))}
      </Box>
    </SectionWrapper>
  );
};

// Languages section component
export const LanguagesSection = ({ languages }) => {
  if (!languages?.length) return null;

  return (
    <SectionWrapper>
      <SectionHeader icon={<Language fontSize="medium" color="info" />} title="Languages" />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {languages.map((language) => (
          <Chip
            key={language}
            label={language}
            sx={{
              backgroundColor: "#f0fdf4",
              color: "#16a34a",
              fontWeight: 500,
            }}
          />
        ))}
      </Box>
    </SectionWrapper>
  );
};

// Certifications section component
export const CertificationsSection = ({ certifications }) => {
  if (!certifications?.length) return null;

  return (
    <SectionWrapper>
      <SectionHeader icon={<Badge fontSize="medium" color="info" />} title="Certifications" />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {certifications.map((cert, index) => (
          <Box key={cert.name || index} sx={{ borderBottom: "1px solid #e5e7eb", pb: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {cert.name}
              </Typography>
              {cert.issuer && (
                <Typography variant="body2" sx={{ color: "gray" }}>
                  Issued by: {cert.issuer}
                </Typography>
              )}
              {Number(cert.year) > 0 && moment(cert.year, "YYYY").isValid() && (
                <Typography variant="body2" sx={{ color: "gray" }}>
                  Year: {cert.year}
                </Typography>
              )}
              {cert.description && (
                <Typography variant="body2" sx={{ color: "gray" }}>
                  {cert.description}
                </Typography>
              )}
              {cert.credentialId && (
                <Typography variant="body2" sx={{ color: "gray" }}>
                  ID: {cert.credentialId}
                </Typography>
              )}
              {cert.issueDate && <DateRange startDate={cert.issueDate} />}
            </Box>
          </Box>
        ))}
      </Box>
    </SectionWrapper>
  );
};

// Licenses section component
export const LicensesSection = ({ licenses }) => {
  if (!licenses?.length) return null;

  return (
    <SectionWrapper>
      <SectionHeader icon={<VerifiedUser fontSize="medium" color="info" />} title="Licenses" />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {licenses.map((license, index) => (
          <Box key={license.name || index} sx={{ borderBottom: "1px solid #e5e7eb", pb: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {license.name}
              </Typography>
              {license.licenseStatus && (
                <Typography
                  variant="body2"
                  sx={{
                    color: license.licenseStatus === "Active" ? "#16a34a" : "gray",
                    fontWeight: license.licenseStatus === "Active" ? 500 : 400,
                  }}
                >
                  Status: {license.licenseStatus}
                </Typography>
              )}
              {license.licenseNumber && (
                <Typography variant="body2" sx={{ color: "gray" }}>
                  License #: {license.licenseNumber}
                </Typography>
              )}
              {license.issuingAuthority && (
                <Typography variant="body2" sx={{ color: "gray" }}>
                  Issued by: {license.issuingAuthority}
                </Typography>
              )}
              <DateRange startDate={license.issueDate} endDate={license.expiryDate} />
            </Box>
          </Box>
        ))}
      </Box>
    </SectionWrapper>
  );
};

// Portfolio section component
export const PortfolioSection = ({ portfolio }) => {
  if (!portfolio?.length) return null;

  return (
    <SectionWrapper>
      <SectionHeader icon={<LinkIcon fontSize="medium" color="info" />} title="Portfolio" />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {portfolio.map((project, index) => (
          <Box key={project.title || index} sx={{ borderBottom: "1px solid #e5e7eb", pb: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {project.title}
              </Typography>
              {project.description && (
                <Typography variant="body2" sx={{ color: "gray" }}>
                  {project.description}
                </Typography>
              )}
              {project.url && (
                <a
                  href={formatUrl(project.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#2563EB", textDecoration: "none" }}
                >
                  View Project
                </a>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </SectionWrapper>
  );
};

// Awards section component
export const AwardsSection = ({ awards }) => {
  if (!awards?.length) return null;

  return (
    <SectionWrapper>
      <SectionHeader icon={<EmojiEvents fontSize="medium" color="info" />} title="Awards" />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {awards.map((award, index) => (
          <Box key={award.name || index} sx={{ borderBottom: "1px solid #e5e7eb", pb: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {award.name}
              </Typography>
              {award.description && (
                <Typography variant="body2" sx={{ color: "gray" }}>
                  {award.description}
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: "gray" }}>
                {award.issuer && award.issuer}{" "}
                {Number(award.year) > 0 &&
                  moment(award.year, "YYYY").isValid() &&
                  `• ${award.year}`}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </SectionWrapper>
  );
};

// References section component
export const ReferencesSection = ({ references }) => {
  if (!references?.length) return null;

  return (
    <SectionWrapper>
      <SectionHeader icon={<Person fontSize="medium" color="info" />} title="References" />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {references.map((reference, index) => (
          <Box
            key={`${reference.firstName}-${reference.lastName}` || index}
            sx={{ borderBottom: "1px solid #e5e7eb", pb: 2 }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {[reference.firstName, reference.lastName].filter(Boolean).join(" ")}
              </Typography>
              {reference.company && (
                <Typography variant="body2" sx={{ color: "gray" }}>
                  {reference.company}
                </Typography>
              )}
              {reference.job && (
                <Typography variant="body2" sx={{ color: "gray" }}>
                  {reference.job}
                </Typography>
              )}
              {reference.email && (
                <IconWrapper>
                  <Email fontSize="small" color="info" />
                  <span>{reference.email}</span>
                </IconWrapper>
              )}
              {reference.phone && (
                <IconWrapper>
                  <Phone fontSize="small" color="info" />
                  <span>{reference.phone}</span>
                </IconWrapper>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </SectionWrapper>
  );
};
